/**
 * AircraftLabelsOverlay - Renders compact sticky labels above ALL aircraft on the map
 *
 * Performance-optimized: React only re-renders when label data changes.
 * Camera movement updates are applied imperatively via style.transform
 * in the shared RAF loop (useMapLabelPositions).
 *
 * Controlled by the rampViewConfigStore which determines which data fields
 * and status-row warnings to show.
 */

import { useEffect, useMemo, useRef, createRef } from 'react'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useRampViewConfigStore } from '@/stores/rampViewConfigStore'
import { buildAllLabelLines, type LabelLine } from '@/lib/utils/format-ramp-labels'
import { TooltipProvider } from '@/components/ui/tooltip'
import { MemoizedAircraftLabel } from './MemoizedAircraftLabel'
import { useLeaderLines } from './LeaderLinesSVG'
import type { PlacedAircraft3D } from '@/services/3d'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { LabelPositionData } from '@/hooks/useMapLabelPositions'

interface AircraftLabelsOverlayProps {
  aircraftData: PlacedAircraft3D[]
  enrichedData: Map<string, AllocationEnrichedData>
  onFrame: (callback: (positions: LabelPositionData) => void) => () => void
}

export function AircraftLabelsOverlay({
  aircraftData,
  enrichedData,
  onFrame,
}: AircraftLabelsOverlayProps) {
  const enabledFields = useRampViewConfigStore((state) => state.enabledFields)
  const enabledStatusGroups = useRampViewConfigStore((state) => state.enabledStatusGroups)
  const disabledStatusRowKeys = useRampViewConfigStore((state) => state.disabledStatusRowKeys)
  const warningDisplayMode = useRampViewConfigStore((state) => state.warningDisplayMode)
  const showLeaderLines = useRampViewConfigStore((state) => state.showLeaderLines)
  const selectedAllocationId = useAircraftEditorStore((state) => state.selectedAllocationId)
  const isDragging = useAircraftEditorStore((state) => state.isDragging)
  const isRotating = useAircraftEditorStore((state) => state.isRotating)

  // Pre-compute label lines for all aircraft (memoized on data + field changes, NOT on camera moves)
  const allLabelLines = useMemo(() => {
    const hasFields = enabledFields.length > 0
    const hasWarnings = warningDisplayMode !== 'off' && enabledStatusGroups.length > 0
    if (!hasFields && !hasWarnings) return new Map<string, LabelLine[]>()

    const result = new Map<string, LabelLine[]>()
    for (const aircraft of aircraftData) {
      const data = enrichedData.get(aircraft.id)
      if (data) {
        const lines = buildAllLabelLines(data, enabledFields, enabledStatusGroups, disabledStatusRowKeys, warningDisplayMode)
        if (lines.length > 0) {
          result.set(aircraft.id, lines)
        }
      }
    }
    return result
  }, [aircraftData, enrichedData, enabledFields, enabledStatusGroups, disabledStatusRowKeys, warningDisplayMode])

  // Build stable list of visible (non-selected) aircraft with labels
  const visibleAircraft = useMemo(() => {
    return aircraftData.filter(
      (ac) => ac.id !== selectedAllocationId && allLabelLines.has(ac.id)
    )
  }, [aircraftData, selectedAllocationId, allLabelLines])

  // Create a stable set of visible IDs for leader lines
  const visibleIdsSet = useMemo(
    () => new Set(visibleAircraft.map((ac) => ac.id)),
    [visibleAircraft]
  )

  // Leader lines hook — SVG element + imperative update
  const { svgElement: leaderLinesSvg, updatePositions: updateLeaderLines } =
    useLeaderLines(showLeaderLines ? visibleIdsSet : new Set())

  // Per-label DOM refs for imperative positioning
  const labelRefsMap = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map())

  // Sync refs map with visible aircraft
  useMemo(() => {
    const newMap = new Map<string, React.RefObject<HTMLDivElement | null>>()
    for (const ac of visibleAircraft) {
      // Reuse existing ref if available, else create new one
      newMap.set(ac.id, labelRefsMap.current.get(ac.id) || createRef<HTMLDivElement>())
    }
    labelRefsMap.current = newMap
  }, [visibleAircraft])

  // Container ref for hiding during drag/rotate
  const containerRef = useRef<HTMLDivElement>(null)

  // Subscribe to the shared RAF position loop
  useEffect(() => {
    if (allLabelLines.size === 0) return

    const unsubscribe = onFrame((positions: LabelPositionData) => {
      // Update each label's transform imperatively
      for (const [id, ref] of labelRefsMap.current) {
        const el = ref.current
        if (!el) continue

        const pos = positions.aircraft.get(id)
        if (!pos || !pos.visible) {
          el.style.transform = 'translate3d(-9999px, -9999px, 0)'
          continue
        }

        el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -100%)`
      }

      // Update leader lines
      if (showLeaderLines) {
        updateLeaderLines(positions.aircraft, positions.aircraftBody)
      }
    })

    return unsubscribe
  }, [onFrame, allLabelLines.size, showLeaderLines, updateLeaderLines])

  // Early exit if nothing to render
  if (allLabelLines.size === 0) {
    return null
  }

  const isInteracting = isDragging || isRotating

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 z-10 pointer-events-none overflow-hidden transition-opacity duration-150 ${
        isInteracting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Leader lines SVG (imperative) */}
      {showLeaderLines && leaderLinesSvg}

      <TooltipProvider delayDuration={300}>
        {visibleAircraft.map((aircraft) => {
          const lines = allLabelLines.get(aircraft.id)
          if (!lines || lines.length === 0) return null

          const acData = enrichedData.get(aircraft.id)
          const isGhost = !!(acData?.allocation.renderMode && acData.allocation.renderMode !== 'solid')

          return (
            <MemoizedAircraftLabel
              key={aircraft.id}
              ref={labelRefsMap.current.get(aircraft.id)}
              lines={lines}
              isGhost={isGhost}
            />
          )
        })}
      </TooltipProvider>
    </div>
  )
}
